import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { OverviewCardDataProps } from '@ctw/shared/content/overview/resource-overview-card';
import { ResourceOverviewCardGrouped } from '@ctw/shared/content/overview/resource-overview-card-grouped';
import { tw } from '@ctw/shared/utils/tailwind';
import { faClipboardList } from '@fortawesome/pro-solid-svg-icons';
import { formatDate } from 'date-fns';
import {
  RenderInfoBadge,
  RenderSyncedWithRecordIcon,
  ResourceTitleColumn,
} from '../resource/resource-title-column';

export type PatientConditionOverviewCardProps = OverviewCardDataProps<ConditionModel>;

/**
 * Presentational conditions overview card. Renders the pre-filtered/sorted list
 * it is handed; owns only the conditions-specific look (grouping, title, icon,
 * per-row rendering). Data fetching lives in `usePatientConditionsOverviewData`.
 */
export const PatientConditionOverviewCard = ({
  data,
  isLoading,
  emptyState,
  footerCTA,
  onRowClick,
}: PatientConditionOverviewCardProps) => (
  <ResourceOverviewCardGrouped
    groupBy={(record) => String(record.ccsChapter)}
    onRowClick={onRowClick}
    data={data}
    emptyState={emptyState}
    footerCTA={footerCTA}
    title="Conditions"
    titleClassName={tw`pl-8`}
    helpText="Chronic Conditions and acute HCCs"
    loading={isLoading}
    headerIcon={faClipboardList}
    testId="conditions-overview"
    renderResource={(record: ConditionModel) => (
      <div className={tw`flex justify-between text-left`}>
        <div className={tw`w-[1.5rem] min-w-[1.5rem] max-w-[1.5rem] text-sm`}>
          {RenderSyncedWithRecordIcon(record.syncedWithRecord)}
        </div>
        <div className={tw`w-full flex-grow text-sm`}>
          <ResourceTitleColumn
            className={tw`w-full`}
            title={
              <>
                {record.display}{' '}
                {record.recordedDate && (
                  <span className={tw`whitespace-nowrap font-normal text-content-subtle text-xs`}>
                    [Last Dx {formatDate(new Date(record.recordedDate), 'yyyy')}]
                  </span>
                )}
              </>
            }
            renderIcon={RenderInfoBadge(
              record.hasHCCv28Codes ? 'HCC' : undefined,
              '',
              record.hasHCCv28Codes ? 'v28' : undefined,
            )}
          />
        </div>
      </div>
    )}
  />
);
